// ============================================================
// Datei: src/pages/ressorts/programm/ProgrammDesktop.jsx
//
// Zweck:
// - Desktop-Ansicht für das Ressort Programm
// - zeigt Libellen, Helferpool, Schichtplan und Gästeliste
//
// ÄNDERUNGEN:
// - Schichtplan wie beim Ressort Kasse aufgebaut
// - Backstagebetreuung und Kanal K als eigene Schichtbereiche
// - autoRefresh bei ShiftTable wieder aktiviert
// - filterFn ergänzt, damit nach Auto-Refresh nur die passende
//   Untergruppe angezeigt wird
// - sichere Prüfung mit Array.isArray(shifts)
// ============================================================

import React, { useState } from "react";

import VolunteerList from "../../../components/VolunteerList";
import ShiftTable from "../../../components/shiftTable/ShiftTable";
import EditVolunteerModal from "../../../components/EditVolunteerModal";

import GuestRegistration from "../../../components/GuestRegistration";
import GuestList from "../../../components/guestList/GuestList";
import EditGuestModal from "../../../components/guestList/EditGuestModal";
import DeleteGuestModal from "../../../components/guestList/DeleteGuestModal";
import VolunteerPool from "../../../components/VolunteerPool";

import "./programm.css";

const ProgrammDesktop = ({
	volunteers,
	setVolunteers,
	shifts,
	guests,
	selectedVolunteer,
	setSelectedVolunteer,
	activeTab,
	setActiveTab,
	reloadGuests,
	reloadVolunteers
}) => {

	// ============================================================
	// STATE
	// ============================================================
	const [guestToEdit, setGuestToEdit] = useState(null);
	const [guestToDelete, setGuestToDelete] = useState(null);

	// ============================================================
	// Sichere Shift-Liste
	//
	// Wichtig:
	// - Beim ersten Rendern können die Schichten noch undefined sein.
	// - Damit filter() nicht crasht, verwenden wir immer ein Array.
	// ============================================================
	const safeShifts = Array.isArray(shifts) ? shifts : [];

	// ============================================================
	// Hilfsfunktion:
	// Prüft, ob eine Schicht innerhalb eines ID-Bereichs liegt.
	// ============================================================
	const isShiftIdBetween = (shift, min, max) => {
		const shiftId = Number(shift?.shiftId);
		return shiftId >= min && shiftId <= max;
	};

	// ============================================================
	// Schichtgruppen Programm
	//
	// Backstagebetreuung:
	// - shift_id 900 bis 919
	//
	// Kanal K:
	// - shift_id 920 bis 949
	// ============================================================
	const backstagebetreuungShifts = safeShifts.filter((shift) =>
		isShiftIdBetween(shift, 900, 919)
	);

	const kanalKShifts = safeShifts.filter((shift) =>
		isShiftIdBetween(shift, 920, 949)
	);

	// ============================================================
	// Tabs
	// ============================================================
	const tabs = [
		{ id: "overview", label: "Übersicht" },
		{ id: "helpers", label: "Libellen" },
		{ id: "shifts", label: "Schichtplan" },
		{ id: "guests", label: "Gästeliste" },
		{ id: "extras", label: "Extrawürscht" }
	];

	return (
		<>
			<div className="tabs-container">
				<div className="tab-bar">
					{tabs.map((tab) => (
						<button
							key={tab.id}
							type="button"
							className={
								"tab-button" +
								(activeTab === tab.id ? " tab-button-active" : "")
							}
							onClick={() => setActiveTab(tab.id)}
						>
							{tab.label}
						</button>
					))}
				</div>

				<div className="tab-panels">

					{/* ====================================================
					    Tab: Libellen
					   ==================================================== */}
					{activeTab === "helpers" && (
						<div className="helpers-layout">
							<div className="helpers-column helpers-column-left">
								<VolunteerList
									volunteers={volunteers}
									setVolunteers={setVolunteers}
									onEdit={(volunteer) => setSelectedVolunteer(volunteer)}
									reloadVolunteers={reloadVolunteers}
								/>
							</div>

							<div className="helpers-column helpers-column-right">
								<VolunteerPool
									departmentName="Programm"
									onAssigned={() => {
										if (reloadVolunteers) {
											reloadVolunteers();
										}
									}}
								/>
							</div>
						</div>
					)}

					{/* ====================================================
					    Tab: Schichtplan

					    Wichtig:
					    - Aufbau ist gleich wie bei Kasse.
					    - autoRefresh bleibt true.
					    - filterFn sorgt dafür, dass ShiftTable nach dem
					      eigenen Nachladen weiterhin nur die richtige
					      Untergruppe anzeigt.
					   ==================================================== */}
					{activeTab === "shifts" && (
						<div className="programm-shifts-panel">

							<section className="programm-shift-section">
								<h2 className="programm-subheading">
									Backstagebetreuung
								</h2>

								<ShiftTable
									shifts={backstagebetreuungShifts}
									departmentName="Programm"
									autoRefresh={true}
									filterFn={(shift) =>
										Number(shift?.shiftId) >= 900 &&
										Number(shift?.shiftId) <= 919
									}
								/>
							</section>

			{/*				<section className="programm-shift-section">
								<h2 className="programm-subheading">
									Kanal K
								</h2>

								<ShiftTable
									shifts={kanalKShifts}
									departmentName="Programm"
									autoRefresh={true}
									filterFn={(shift) =>
										Number(shift?.shiftId) >= 920 &&
										Number(shift?.shiftId) <= 949
									}
								/>
							</section>
			*/}
						</div>
					)}

					{/* ====================================================
					    Tab: Gästeliste
					   ==================================================== */}
					{activeTab === "guests" && (
						<>
							<GuestRegistration refreshGuestList={reloadGuests} />

							<GuestList
								guests={guests}
								refreshGuestList={reloadGuests}
								onEdit={(guest) => setGuestToEdit(guest)}
								onDelete={(guest) => setGuestToDelete(guest)}
							/>
						</>
					)}

					{/* ====================================================
					    Tab: Extrawürscht
					   ==================================================== */}
					{activeTab === "extras" && (
						<div></div>
					)}
				</div>
			</div>

			{/* ============================================================
			    Modal: Volunteer bearbeiten
			   ============================================================ */}
			{selectedVolunteer && (
				<EditVolunteerModal
					volunteer={selectedVolunteer}
					onClose={() => setSelectedVolunteer(null)}
					onSaved={reloadVolunteers}
				/>
			)}

			{/* ============================================================
			    Modal: Gast bearbeiten
			   ============================================================ */}
			{guestToEdit && (
				<EditGuestModal
					guest={guestToEdit}
					onClose={() => setGuestToEdit(null)}
					onSaved={() => {
						setGuestToEdit(null);
						reloadGuests();
					}}
				/>
			)}

			{/* ============================================================
			    Modal: Gast löschen
			   ============================================================ */}
			{guestToDelete && (
				<DeleteGuestModal
					guest={guestToDelete}
					onClose={() => setGuestToDelete(null)}
					onDeleted={() => {
						setGuestToDelete(null);
						reloadGuests();
					}}
				/>
			)}
		</>
	);
};

export default ProgrammDesktop;